import { type ILogger } from '@trimble/logging';
import { type PointCloudRoot } from './feature-extraction-types';
import { type FeatureExtractionContextType } from '@mobile-mapping/providers';
import { Accessor, batch } from 'solid-js';
import { type LonLatToSceneConverter } from '@trimble/web3d-providers';
import {
  type GeospatialFeature,
  type PointCloudCrsAuthoring,
} from '@trimble/geospatial';

const MIGRATION_MAX_VALIDATION_ERROR_M = 1;
const MIN_XY_COORDINATE_LENGTH = 2;

export interface CreatePointCloudCrsAuthoringParams {
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  readonly csib: string;
  readonly pointCloudRoots: Accessor<PointCloudRoot[]>;
  ensureConverter(): Promise<LonLatToSceneConverter | undefined>;
}

export interface PointCloudCrsAuthoringHandle {
  getPointCloudCrsAuthoring(): PointCloudCrsAuthoring | undefined;
  dispose(): void;
}

function firstFiniteXY(
  coordinates: readonly (readonly [number, number, number?])[]
): readonly [number, number] | undefined {
  const first = coordinates[0];
  if (
    !first ||
    first.length < MIN_XY_COORDINATE_LENGTH ||
    !Number.isFinite(first[0]) ||
    !Number.isFinite(first[1])
  ) {
    return undefined;
  }
  return [first[0], first[1]];
}

/** @returns the validated point-cloud CRS URN, or undefined to stay on WGS-84. */
async function resolveValidatedPointCloudCrs(
  context: FeatureExtractionContextType,
  converter: LonLatToSceneConverter,
  logger: ILogger,
  logLabel: string
): Promise<string | undefined> {
  const scope = context.scope();
  if (!scope) {
    return undefined;
  }
  const featureRepository = context.featureRepository();
  const [globalFeatures, originalFeatures] = await Promise.all([
    featureRepository.loadAllFeatures({ projectId: scope.projectId }, 'global'),
    featureRepository.loadAllFeatures(
      { projectId: scope.projectId },
      'original'
    ),
  ]);
  const originalById = new Map(
    originalFeatures
      .filter((feature): feature is GeospatialFeature & { id: string } =>
        Boolean(feature.id)
      )
      .map((feature) => [feature.id, feature])
  );
  const reference = globalFeatures.find(
    (feature) =>
      Boolean(feature.id) &&
      feature.geometry.type === 'LineString' &&
      Boolean(feature.crsUrn)
  );
  const referenceOriginal = reference?.id
    ? originalById.get(reference.id)
    : undefined;
  if (
    !reference?.crsUrn ||
    reference.geometry.type !== 'LineString' ||
    referenceOriginal?.geometry.type !== 'LineString'
  ) {
    logger.warn(`${logLabel}: no point cloud CRS reference feature; skipping`);
    return undefined;
  }
  // Original is projected CRS; Global is WGS-84 and would mis-tag X,Y,Z.
  const targetCrs = referenceOriginal.crsUrn ?? context.pointCloudCrs();
  if (!targetCrs) {
    logger.warn(
      `${logLabel}: reference has no projected point cloud CRS; skipping`
    );
    return undefined;
  }
  const referenceLonLat = firstFiniteXY(reference.geometry.coordinates);
  const referenceStoredXY = firstFiniteXY(
    referenceOriginal.geometry.coordinates
  );
  if (!referenceLonLat || !referenceStoredXY) {
    logger.warn(`${logLabel}: reference has malformed coordinates; skipping`, {
      referenceId: reference.id,
    });
    return undefined;
  }
  const [refLon, refLat] = referenceLonLat;
  const [storedX, storedY] = referenceStoredXY;
  const refScene = converter.lonLatToScene(refLon, refLat);
  if (!refScene) {
    logger.warn(`${logLabel}: converter could not resolve reference; skipping`);
    return undefined;
  }
  const errorMeters = Math.hypot(refScene.x - storedX, refScene.y - storedY);
  logger.info(`${logLabel}: point cloud CRS validation`, {
    referenceId: reference.id,
    targetCrs,
    convertedXY: { x: refScene.x, y: refScene.y },
    storedXY: [storedX, storedY],
    errorMeters,
    toleranceMeters: MIGRATION_MAX_VALIDATION_ERROR_M,
  });
  if (errorMeters > MIGRATION_MAX_VALIDATION_ERROR_M) {
    logger.warn(
      `${logLabel}: converter does not reproduce stored point cloud coordinates; skipping to avoid corrupting geometry`,
      { errorMeters, toleranceMeters: MIGRATION_MAX_VALIDATION_ERROR_M }
    );
    return undefined;
  }
  return targetCrs;
}

export function createPointCloudCrsAuthoring(
  params: CreatePointCloudCrsAuthoringParams
): PointCloudCrsAuthoringHandle {
  const { context, logger, csib, pointCloudRoots, ensureConverter } = params;
  // Point-cloud CRS authoring is enabled only once a TGL round-trip validates
  // it against a stored reference feature (see enablePointCloudCrsAuthoring);
  // until then, and whenever validation fails, authoring falls back to WGS-84.
  let pointCloudCrsAuthoring: PointCloudCrsAuthoring | undefined;
  let disposed = false;

  const enablePointCloudCrsAuthoring = async (): Promise<void> => {
    const converter = await ensureConverter();
    if (!converter) {
      return;
    }
    try {
      const targetCrs = await resolveValidatedPointCloudCrs(
        context,
        converter,
        logger,
        'point cloud CRS authoring'
      );
      if (!targetCrs || disposed) {
        return;
      }
      pointCloudCrsAuthoring = {
        toCoordinates: (x, y, z) => [x, y, z],
        crsUrn: targetCrs,
      };
      batch(() => {
        pointCloudRoots()
          .filter((root) => root.csib === csib)
          .forEach((root) => {
            context.setPointCloudCrs(root.id, targetCrs);
          });
      });
      logger.info('Point cloud CRS authoring enabled', { targetCrs });
    } catch (error) {
      logger.warn(
        'Point cloud CRS authoring validation failed; authoring stays WGS-84',
        undefined,
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };

  void enablePointCloudCrsAuthoring();

  return {
    getPointCloudCrsAuthoring: () => pointCloudCrsAuthoring,
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
    },
  };
}
